import React, { useState, useEffect } from "react";
import "~/css/Modal.css";
import "~/css/Explanation.css";
import { useTranslation } from "react-i18next";
import * as image from "~/Images";
import { useAtom } from "jotai";
import { layoutSelectAtom } from "~/Atom";

/**
 * 縦画面か判定し、説明の表示を切り替えるフック
 * @return 縦画面ならtrue、横画面ならfalse
 */
const useIsPortrait = (): boolean => {
  const [isPortrait, setIsPortrait] = useState<boolean>(() => {
    if (typeof window === "undefined") {
      return true;
    }
    return window.matchMedia("(orientation: portrait)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const mq = window.matchMedia("(orientation: portrait)");
    const handler = () => setIsPortrait(mq.matches);

    mq.addEventListener("change", handler);
    return () => {
      mq.removeEventListener("change", handler);
    };
  }, []);

  return isPortrait;
};

/**
 * 説明モーダルコンポーネント
 * アプリケーションの使用方法や機能説明を表示するモーダル
 * @returns React.JSX.Element
 */
const Explanation: React.FC = () => {
  const { t } = useTranslation();
  const [currentImage] = useState(1);
  const isPortrait = useIsPortrait();
  const [layoutSelect] = useAtom(layoutSelectAtom);
  // スライド方向（アニメーション制御用）
  const [direction, setDirection] = useState<"next" | "prev" | null>(null);

  /**
   * アニメーション終了時の処理
   * スライド方向状態をリセット
   * @returns void
   */
  const handleAnimationEnd = () => {
    setDirection(null);
  };

  // 横画面用の説明キーリスト
  const landscapeKeys = [
    "expCurrentTime", // 1
    "expFontSize", // 2
    "expLanguage", // 3
    "expCharacterName", // 4
    "expVolume", // 5
    "expChangeButton", // 6
    "expIndicator", // 7 // レイアウトによって表示しない要素
    "expChatPage", // 8
    "expTextBox", // 9
    "expSetting", // 10
    "expExpandPanel", // 11
    "expGoogleMap", // 12
    "expFloorMap", // 13
    "expExplanation", // 14
    "expReload", // 15
    "expQrCode", // 16
    "expProblemOccurs", // 17
  ];

  // 縦画面用の説明キーリスト
  const portraitKeys = [
    "expCurrentTime", // 1
    "expLanguage", // 2
    "expFontSize", // 3
    "expCharacterName", // 4
    "expIndicator", // 5  // レイアウトによって表示しない要素
    "expVolume", // 6
    "expChangeButton", // 7
    "expChatPage", // 8
    "expTextBox", // 9
    "expGoogleMap", // 10
    "expFloorMap", // 11
    "expExplanation", // 12
    "expReload", // 13
    "expQrCode", // 14
    "expProblemOccurs", // 15
    "expSetting", // 16
  ];

  // レイアウトパターンに応じたキーのフィルタリング
  let layoutPattern;
  let filteredLandscapeKeys = landscapeKeys;
  let filteredPortraitKeys = portraitKeys;

  switch (layoutSelect) {
    case 1:
      layoutPattern = "1";
      // レイアウト1の縦画面はインジケータ表示がないため、説明文も削除(4要素目)
      filteredPortraitKeys = portraitKeys.filter((_, index) => index !== 4);
      break;
    case 2:
      layoutPattern = "2";
      // レイアウト2の両画面はインジケータ表示がないため、説明文も削除(6/4要素目)
      filteredLandscapeKeys = landscapeKeys.filter((_, index) => index !== 6);
      filteredPortraitKeys = portraitKeys.filter((_, index) => index !== 4);
      break;
    case 3:
      layoutPattern = "3";
      break;
    default:
      layoutPattern = "Default";
  }

  // レイアウトに対応する説明用画像の配列
  const explanationImages = {
    landscapeImage:
      image[`explanationLandscapeLayout${layoutPattern}` as keyof typeof image] ||
      image.explanationLandscapeLayoutDefault,
    portraitImage:
      image[`explanationPortraitLayout${layoutPattern}` as keyof typeof image] ||
      image.explanationPortraitLayoutDefault,
  };

  /**
   * 指定した数値に対応する丸数字を取得する
   * @param n 取得したい丸数字の数値（1〜35）
   * @returns 丸数字（文字列）
   */
  const getCircledNumber = (n: number): string => {
    const circledDigitOne = 0x2460; // ①
    const parenthesizedDigitTwentyOne = 0x3251; // ㉑
    if (n >= 1 && n <= 20) {
      return String.fromCharCode(circledDigitOne + (n - 1)); // ①..⑳
    }
    if (n >= 21 && n <= 35) {
      return String.fromCharCode(parenthesizedDigitTwentyOne + (n - 21)); // ㉑..㉟ (21..35)
    }
    return `(${n})`;
  };

  const keys = isPortrait ? filteredPortraitKeys : filteredLandscapeKeys;

  return (
    <>
      {/* モーダルコンテンツ */}
      <div className="modal-content explanation-modal-content">
        <div className="explanation-content">
          <div className={`explanation-image-container ${direction}`}>
            {/* 説明画像 */}
            <img
              src={isPortrait ? explanationImages.portraitImage : explanationImages.landscapeImage}
              className="explanation-image"
              alt={`${t("explanationImage")} ${currentImage}`}
              onAnimationEnd={handleAnimationEnd}
            />

            {/* 説明リスト */}
            <div className="explanation-list">
              {keys.map((k, i) => (
                <p key={k}>
                  <span className="number">{getCircledNumber(i + 1)}</span> {t(k)}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Explanation;
